import { APICallError, NoObjectGeneratedError, RetryError } from 'ai';
import { toast } from 'sonner';

import { type ErrorCategory, FRIENDLY_ERRORS, isProviderQuotaMessage } from './friendly-errors';
import { VIEWER_READ_ONLY_TITLE, viewerReadOnlyMessage } from './viewer-read-only';

import { logger } from '@/utils/logger';

export interface ErrorContext {
  feature: string;
  page: string;
  sessionId?: string;
  retry?: () => void;
}

function unwrapCause(error: unknown): unknown {
  let current = error;
  while (current && current instanceof Error && current.cause instanceof Error) {
    current = current.cause;
  }
  return current;
}

export function classifyError(error: unknown): ErrorCategory {
  const unwrapped = unwrapCause(error);

  // A RetryError is a wrapper, not a cause: classify what failed on the last
  // attempt, and fall back to timeout only when that says nothing more.
  // Treating every RetryError as a timeout reported three 429 quota refusals
  // as "The AI took too long" (2026-10-21).
  if (RetryError.isInstance(unwrapped)) {
    const inner = unwrapped.lastError ? classifyError(unwrapped.lastError) : 'unknown';
    return inner === 'unknown' ? 'timeout' : inner;
  }

  if (APICallError.isInstance(unwrapped)) {
    const code = unwrapped.statusCode;
    const body = `${unwrapped.message} ${unwrapped.responseBody ?? ''}`.toLowerCase();
    if (code === 429 && isProviderQuotaMessage(body)) return 'provider_quota';
    if (code === 429) return 'rate_limit';
    if (code === 401 || code === 403) return 'auth';
    if (code === 408) return 'timeout';
    if (code >= 500) return 'unknown';
  }

  if (NoObjectGeneratedError.isInstance(unwrapped)) return 'schema_validation';

  const msg = error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();

  // Checked before every other message-based branch below. This mirrors the
  // same category in friendly-errors.ts's categorizeRawError (see the comment
  // there for the full rationale): no exact Gemini/Vertex "input token count
  // exceeds" string exists anywhere in the installed AI SDK packages (checked
  // node_modules — it's a runtime API response value, not SDK source), so this
  // matches our own pre-flight errorCode plus a conservative compound
  // heuristic requiring "token"/"context" co-occurring with "exceed"/"maximum",
  // never a single bare keyword that something unrelated (e.g. a 429 "quota
  // exceeded" message, which this file has no separate quota branch for
  // today, but could grow one) could coincidentally trip.
  if (
    msg.includes('context_length_exceeded') ||
    msg.includes('context length exceeded') ||
    (msg.includes('context window') && (msg.includes('exceed') || msg.includes('maximum'))) ||
    (msg.includes('token') && msg.includes('context') && msg.includes('exceed'))
  )
    return 'context_length_exceeded';

  // F-107 (UAT 2026-06-13): the chat transport surfaces a 401 from /api/agent
  // as a PLAIN Error whose message is the response body — `{"error":"Unauthorized"}`
  // — NOT an APICallError, so the statusCode branch above misses it and it used
  // to fall through to `unknown` ("Something unexpected happened"). That's the
  // exact useless message Clayton saw when his long-open-tab token expired
  // (Sentry: `ed.sendMessages {"error":"Unauthorized"}`). Classify it as `auth`
  // so the toast reads "Session expired / Sign in again" — and so the chat hook
  // can detect it and silently refresh+retry before any toast fires.
  if (
    msg.includes('unauthorized') ||
    msg.includes('jwt expired') ||
    msg.includes('session expired') ||
    /\b401\b/.test(msg)
  )
    return 'auth';

  // Gemini 3 thought-signature — library races where the signature is
  // dropped between tool-loop steps. Retry almost always succeeds.
  if (
    msg.includes('thought_signature') ||
    msg.includes('thoughtsignature') ||
    msg.includes('missing a thought')
  )
    return 'gemini_signature';

  if (msg.includes('safety') || msg.includes('blocked') || msg.includes('content filter'))
    return 'content_blocked';

  // URL fetch refused / blocked / SSRF / user-agent rejected.
  if (
    msg.includes("couldn't open") ||
    msg.includes('ssrf') ||
    msg.includes('fetch blocked') ||
    msg.includes('refused to connect') ||
    msg.includes('robots.txt') ||
    /fetch[- ]url.*fail/.test(msg)
  )
    return 'url_fetch_blocked';

  if (error instanceof Error && error.name === 'AbortError') return 'timeout';

  // `navigator.onLine` is only meaningful in a browser. Node 21+ (Vercel runs
  // nodejs22.x) ships a global `navigator` whose `onLine` is `undefined`, so
  // the old `!navigator.onLine` check evaluated to `true` for EVERY server-side
  // error — misclassifying all agent failures as `network` and surfacing the
  // useless "Connection lost. Check your internet" message while masking the
  // real cause. Gate on `onLine` actually being a boolean (browser only).
  const browserOffline =
    typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean' && !navigator.onLine;
  if (
    browserOffline ||
    msg.includes('failed to fetch') ||
    msg.includes('fetch failed') ||
    msg.includes('network error')
  )
    return 'network';

  // Tool-level `state: 'error'` that bubbled up to the agent loop.
  if (msg.includes('tool error') || msg.includes('tool returned error')) return 'tool_failed';

  return 'unknown';
}

export function handleError(error: unknown, ctx: ErrorContext): void {
  const viewerMessage = viewerReadOnlyMessage(error);
  if (viewerMessage) {
    toast.error(VIEWER_READ_ONLY_TITLE, { description: viewerMessage });
    return;
  }

  const category = classifyError(error);
  const friendly = FRIENDLY_ERRORS[category];

  const toastOptions: Record<string, unknown> = {
    description: friendly.description,
  };

  if (friendly.action === 'retry' && ctx.retry) {
    toastOptions.action = { label: 'Try again', onClick: ctx.retry };
  } else if (friendly.action === 'report') {
    toastOptions.action = {
      label: 'Report issue',
      onClick: () => window.open('mailto:hello@mora-marketer.com'),
    };
  } else if (friendly.action === 'reauth') {
    toastOptions.action = {
      label: 'Sign in',
      onClick: () => window.location.assign('/auth'),
    };
  }

  toast.error(friendly.title, toastOptions);

  if (import.meta.env.PROD) {
    import('@sentry/react')
      .then((Sentry) => {
        Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
          tags: { feature: ctx.feature, page: ctx.page, error_category: category },
          extra: { sessionId: ctx.sessionId },
        });
      })
      .catch(() => {});
  }

  logger.error('[handleError]', category, error);
}
